import React, { Fragment, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import ClipCard from '../ClipCard/ClipCard';
import styles from './ClipsList.module.scss';
import SortableItemsContainer from '../Sortable/SortableItemsContainer';
import SortableItem from '../Sortable/SortableItem';
import ScrollContainer from '../ScrollContainer/ScrollContainer';
import ClipCardIntroOutro from '../ClipCardIntroOutro/ClipCardIntroOutro';
import AddClipAndTransitionButton from '../AddClipAndTransitionButton/AddClipAndTransitionButton';
import TransitionCard from '../TransitionCard/TransitionCard';
import { START_LIST_HEIGHT_FOR_MINI } from '../../constants/editorConst';

const ClipsList = ({
  isDraft,
  intro,
  outro,
  cards,
  handleDotsClick,
  handleSelectClip,
  currentVideoPart,
  showMenu,
  onSortEnd = null,
  saving,
  onAddClip,
  onAddEmptyClip,
  onUploadClip,
  onAddTransition,
  activeTransitionRightMedia,
  setActiveTransitionRightMedia,
  onDeleteTransition,
  transitions,
  allowTransitions = true,
  allowAddClip = true,
  onBackgroundMusicChange,
  disableNonClipCards,
  handleRecordScene,
  userPermissions = null,
  isEditingRightSidebar,
  className,
  clipsListStyles,
  hasChanged,
}) => {
  const [isMoving, setIsMoving] = useState(false);
  const listRef = useRef(null);

  // We have property 'pointer-events: none' in the card style and the useOutsideClick hook simply doesn't work on card
  // But we cannot remove this poperty because for some reason, drag and drop for clips list doesn't work without it
  // So let's add this flag to know if the menu is open and temporarily disable the 'pointer-events: none' property until the menu is closed
  const [externalAddMenuShow, setExternalAddMenuShow] = useState(false);

  const handleSortEnd = (...args) => {
    setIsMoving(false);
    onSortEnd && onSortEnd(...args);
  };

  const [hoverCardId, setHoverCardId] = useState(-1);
  const [mouseInScrollSlider, setMouseInScrollSlider] = useState(false);

  const [showMinCard, setShowMinCard] = useState(false);

  const updateStoryPermission = useMemo(() => userPermissions?.isOwner || userPermissions?.isFolderEditor || userPermissions?.updateStory, [userPermissions]);

  const getScenePermission = useCallback((card) => {
    return updateStoryPermission || (userPermissions?.allowedScenes.find(id => id == card.id) !== undefined && card.trimmedDuration && card.video);
  }, [updateStoryPermission, userPermissions]);

  const changeExternalAddMenuShow = value => {
    setExternalAddMenuShow(value);
  };

  const handleHoverCard = useCallback(
    id => {
      setHoverCardId(id);
    },
    [hoverCardId]
  );

  const handleContainerHeightChanged = value => {
    setShowMinCard(value <= START_LIST_HEIGHT_FOR_MINI);
  };

  const mouseLeaveScrollContainer = () => {
    setHoverCardId(null);
  };

  useEffect(() => {
    const horizontalScrollListener = e => {
      handleDotsClick();
    };

    if (showMenu?.id) {
      listRef.current.addEventListener('scroll', horizontalScrollListener);
    } else {
      listRef.current.removeEventListener('scroll', horizontalScrollListener);
    }

    return () => listRef.current.removeEventListener('scroll', horizontalScrollListener);
  }, [showMenu]);

  return (
    <SortableItemsContainer
      axis="xy"
      useDragHandle={true}
      helperClass={styles.sortable}
      onSortStart={() => setIsMoving(true)}
      onSortEnd={handleSortEnd}
    >
      <ScrollContainer
        className={className}
        setMouseInScrollSlider={setMouseInScrollSlider}
        mouseLeaveScrollContainer={mouseLeaveScrollContainer}
        innerStyles={clipsListStyles}
        innerRef={listRef}
        innerRefListenerCallback={handleContainerHeightChanged}
      >
        {intro ? (
          <ClipCardIntroOutro
            clipIndex={'intro'}
            clip={intro}
            handleSelectClip={handleSelectClip}
            selectedClip={!activeTransitionRightMedia && currentVideoPart === 'intro'}
            disabled={disableNonClipCards}
            showMinCard={showMinCard}
          />
        ) : null}
        {cards &&
          cards
            .filter(item => item.id !== 'Intro' && item.id !== 'Outro')
            .map((card, index, array) => (
              <Fragment key={card.id}>
                {index > 0 && transitions?.find(item => item.rightMediaId === card.id) ? (
                  <TransitionCard
                    transition={transitions?.find(item => item.rightMediaId === card.id)}
                    saving={saving}
                    active={activeTransitionRightMedia === card.id}
                    index={card.id}
                    handleTransitionClicked={setActiveTransitionRightMedia}
                    isMoving={isMoving}
                    handleDeleteTransition={onDeleteTransition}
                    disabled={disableNonClipCards || !updateStoryPermission}
                    showMinCard={showMinCard}
                  />
                ) : null}
                {(transitions?.find(item => item.rightMediaId === card.id) || index === 0) && (
                  <AddClipAndTransitionButton
                    index={index}
                    before={true}
                    handleAddClip={onAddClip}
                    handleRecordScene={handleRecordScene}
                    handleAddTransition={onAddTransition}
                    handleAddEmptyClip={onAddEmptyClip}
                    handleUploadClip={onUploadClip}
                    showAddTransition={allowTransitions && !transitions?.find(item => item.rightMediaId === card.id)}
                    showAddItem={allowAddClip}
                    isEditingAction={isEditingRightSidebar}
                    disabled={!updateStoryPermission || saving}
                    changeExternalAddMenuShow={changeExternalAddMenuShow}
                    hoverCardId={hoverCardId}
                    mouseInScrollSlider={mouseInScrollSlider}
                    hasChanged={hasChanged}
                  />
                )}
                <SortableItem
                  key={card.id}
                  index={index}
                  itemId={card.id}
                  tabIndex={0}
                  disabled={card.processing || saving || isEditingRightSidebar || !updateStoryPermission}
                >
                  <ClipCard
                    isDraft={isDraft}
                    handleSelectClip={handleSelectClip}
                    selectedClip={!activeTransitionRightMedia && currentVideoPart === index.toString()}
                    clip={card}
                    author={card?.author ?? card?.owner}
                    clipIndex={index}
                    handleDotsClick={getScenePermission(card) && handleDotsClick}
                    showMenu={showMenu}
                    externalAddMenuShow={externalAddMenuShow}
                    saving={saving}
                    disableHover={saving}
                    cards={cards}
                    isEditingAction={isEditingRightSidebar}
                    lastCard={index === cards?.length - 1 && !outro}
                    onBackgroundMusicChange={onBackgroundMusicChange}
                    isAutoScroll={true}
                    handleHoverCard={handleHoverCard}
                    showMinCard={showMinCard}
                  />
                </SortableItem>
                <AddClipAndTransitionButton
                  index={index}
                  handleRecordScene={handleRecordScene}
                  lastCard={index === cards?.filter(item => item.id !== 'Intro').length - 1}
                  handleAddTransition={onAddTransition}
                  handleAddClip={onAddClip}
                  handleAddEmptyClip={onAddEmptyClip}
                  handleUploadClip={onUploadClip}
                  isEditingAction={isEditingRightSidebar}
                  showAddItem={allowAddClip}
                  showAddTransition={
                    allowTransitions && !transitions?.find(item => item.leftMediaId === card.id) && index !== array.length - 1
                  }
                  disabled={!updateStoryPermission || saving}
                  changeExternalAddMenuShow={changeExternalAddMenuShow}
                  hoverCardId={hoverCardId}
                  mouseInScrollSlider={mouseInScrollSlider}
                  hasTransitionAfter={transitions?.find(item => item.leftMediaId === card.id)}
                  hasChanged={hasChanged}
                />
              </Fragment>
            ))}
        {outro ? (
          <ClipCardIntroOutro
            clipIndex={'outro'}
            clip={outro}
            handleSelectClip={handleSelectClip}
            selectedClip={!activeTransitionRightMedia && currentVideoPart === 'outro'}
            outro={true}
            disabled={disableNonClipCards}
            showMinCard={showMinCard}
          />
        ) : null}
      </ScrollContainer>
    </SortableItemsContainer>
  );
};

export default ClipsList;
